import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../utils/api";
import { motion, AnimatePresence } from "framer-motion";
import { FaBalanceScale } from "react-icons/fa";

const normalizePhone = (value) => String(value || "").replace(/\D/g, "");

function Register() {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const [ref, setRef] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ لو في كود إحالة في الرابط
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refParam = params.get("ref");
    if (refParam) setRef(refParam);
  }, [location]);

  // ✅ Regex رقم مصر
  const egyptPhoneRegex = /^01[0125][0-9]{8}$/;

  /* ===========================================================
     ✅ التحقق من البريد الإلكتروني + رقم الموبايل (Pre-check فقط)
  =========================================================== */
  const handleRegister = async () => {
    const normalizedPhone = normalizePhone(form.phone);

    if (!form.name || !form.email || !form.password || !normalizedPhone) {
      alert("⚠️ من فضلك املأ كل البيانات المطلوبة.");
      return;
    }

    if (!egyptPhoneRegex.test(normalizedPhone)) {
      alert("❌ رقم الموبايل غير صالح (مثال صحيح: 01012345678)");
      return;
    }

    try {
      setLoading(true);

      // ✅ تحقق من البريد
      const emailRes = await api.post("/auth/check-email", {
        email: form.email,
      });
      if (emailRes.data?.exists) {
        alert("⚠️ هذا البريد مسجل بالفعل، يرجى تسجيل الدخول.");
        return;
      }

      // ✅ تحقق من رقم الموبايل
      const phoneRes = await api.post("/auth/check-phone", {
        phone: normalizedPhone,
      });
      if (phoneRes.data?.exists) {
        alert("⚠️ رقم الموبايل مستخدم بالفعل، يرجى استخدام رقم آخر.");
        return;
      }

      // ===================================================
      // ✅ التعديل الوحيد (إضافة referredBy)
      // ===================================================
      const pendingData = {
        ...form,
        phone: normalizedPhone,
        referredBy: ref || null,
      };

      localStorage.setItem("pendingUserData", JSON.stringify(pendingData));

      // ✅ فتح نافذة الاشتراك
      setShowSubModal(true);
    } catch (err) {
      console.error("❌ Register check error:", err);
      alert(
        err?.response?.data?.message ||
          "حدث خطأ أثناء التحقق من البريد أو رقم الموبايل."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===========================================================
     💳 الانتقال لصفحة الاشتراك (بدون Reload)
  =========================================================== */
  const goToSubscription = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    setShowSubModal(false);

    setTimeout(() => {
      try {
        navigate("/subscribe", { replace: true });
      } catch (err) {
        console.warn("⚠️ navigate فشل، استخدام fallback:", err);
        window.history.pushState({}, "", "/subscribe");
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    }, 200);
  };

  return (
    <div
      className="flex items-center justify-center h-screen w-full bg-cover bg-center relative px-4"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1600334129128-685c5a7b4f01?auto=format&fit=crop&w=1500&q=80')",
      }}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 bg-gray-900/95 border border-yellow-500 p-8 md:p-10 rounded-2xl shadow-2xl w-full max-w-lg text-center"
      >
        <div className="flex flex-col items-center mb-6">
          <FaBalanceScale className="text-yellow-400 text-5xl mb-3" />
          <h2 className="text-3xl font-bold text-yellow-400 tracking-wide">
            إنشاء حساب جديد
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            الخطوة الأولى لتفعيل حسابك داخل النظام ⚖️
          </p>
        </div>

        <div className="space-y-4">
          <input
            type="text"
            placeholder="الاسم الكامل"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full p-3 bg-gray-800 rounded-md border border-gray-700 text-white focus:ring-2 focus:ring-yellow-400 outline-none placeholder-gray-400"
          />

          <input
            type="email"
            placeholder="البريد الإلكتروني"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full p-3 bg-gray-800 rounded-md border border-gray-700 text-white focus:ring-2 focus:ring-yellow-400 outline-none placeholder-gray-400"
          />

          <input
            type="tel"
            inputMode="numeric"
            placeholder="رقم الموبايل (مثال: 01012345678)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full p-3 bg-gray-800 rounded-md border border-gray-700 text-white focus:ring-2 focus:ring-yellow-400 outline-none placeholder-gray-400"
          />

          <input
            type="password"
            placeholder="كلمة المرور"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full p-3 bg-gray-800 rounded-md border border-gray-700 text-white focus:ring-2 focus:ring-yellow-400 outline-none placeholder-gray-400"
          />
        </div>

        {ref && (
          <div className="bg-green-600/20 border border-green-500 text-green-400 text-sm p-3 rounded-md mt-3">
            🎉 تم ربط حسابك بدعوة من المحامي رقم:{" "}
            <b className="text-green-300">{ref}</b>
          </div>
        )}

        <button
          onClick={handleRegister}
          disabled={loading}
          className={`w-full mt-6 font-bold py-3 rounded-lg shadow-md transition-all ${
            loading
              ? "bg-gray-600 text-gray-300 cursor-not-allowed"
              : "bg-yellow-500 hover:bg-yellow-400 text-black"
          }`}
        >
          {loading ? "⏳ جاري التحقق..." : "متابعة التسجيل"}
        </button>

        <p className="text-sm mt-6 text-gray-400">
          لديك حساب بالفعل؟{" "}
          <Link
            to="/login"
            className="text-yellow-400 hover:underline transition-all"
          >
            تسجيل الدخول
          </Link>
        </p>
      </motion.div>

      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-white rounded-2xl shadow-2xl p-8 text-center w-[90%] max-w-md"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-3">
                💳 تأكيد الاشتراك
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                لإتمام التسجيل وتفعيل حسابك، يرجى اختيار خطة اشتراك وإتمام الدفع
                عبر{" "}
                <span className="text-yellow-500 font-semibold">PaySky</span>.
              </p>

              <div className="flex justify-center gap-4">
                <button
                  onClick={goToSubscription}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-bold"
                >
                  ✅ اشترك الآن
                </button>
                <button
                  onClick={() => setShowSubModal(false)}
                  className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-bold"
                >
                  إلغاء
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Register;
